import React, { useState, useEffect } from 'react'
import { useCachedImage } from '@renderer/hooks/useCachedImage'

interface PhotoFrameProps {
  src: string
  alt?: string
  displayRatio?: number // The ratio to display in (e.g. 1/1)
  className?: string
  isMirrored?: boolean
}

const PhotoFrame: React.FC<PhotoFrameProps> = ({
  src,
  alt = '',
  displayRatio = 1,
  className = '',
  isMirrored = false
}) => {
  const [sourceRatio, setSourceRatio] = useState<number | null>(null)
  const cachedSrc = useCachedImage(src)

  useEffect(() => {
    const img = new Image()
    img.onload = (): void => {
      setSourceRatio(img.width / img.height)
    }
    img.src = cachedSrc
  }, [cachedSrc])

  // Container style maintains the display ratio
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    aspectRatio: `${displayRatio}`,
    overflow: 'hidden'
  }

  // Calculate image dimensions to maintain source ratio while filling display ratio
  // We want to fill the container completely and crop excess parts
  const imageStyle: React.CSSProperties = sourceRatio
    ? {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: `translate(-50%, -50%) ${isMirrored ? 'scaleX(-1)' : ''}`,
        transformOrigin: 'center',
        width: sourceRatio > displayRatio ? `${(sourceRatio / displayRatio) * 100}%` : '100%',
        height: sourceRatio > displayRatio ? '100%' : `${(displayRatio / sourceRatio) * 100}%`,
        objectFit: 'cover'
      }
    : {
        visibility: 'hidden' // Hide image until we know its ratio
      }

  return (
    <div style={containerStyle} className={className}>
      <img src={cachedSrc} alt={alt} style={imageStyle} crossOrigin="anonymous" />
    </div>
  )
}

export default PhotoFrame
